// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LabelFormat } from './label-format';
import { LabelEditorUtils } from './label-editor-utils';
import { DefaultTextParameters } from './default-text-parameters';
import { ConstraintCheckUtils } from './constraint-check-validation/constraint-check-utils';
import { ConstraintCheckResults } from './constraint-check-validation/constraint-check-results';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { TextSizeCheckVO } from '@3d-scene/manager/text-size-check.vo';
import { TextSizeCheckUtils } from '@shared/util/text-size-check-utils';
import { CellLabelSizeInfoUtils } from '@shared/util/cell-label-size-info-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { MetaElementUsageVO, MetaElementVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { StringUtils } from '@shared/util/string-utils';
import { TextCheckSpacingData } from '@3d-scene/manager/text-check-spacing-data';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConfiguratorParameters } from '@3d-scene/configurator-parameters/configurator-parameters';
import { Log } from '@shared/debug/log';
import { LabelHtmlSanitizer } from '@project/components/label/label-editor/label-html-sanitizer';
import { DirectPrintingService } from '../direct-printing.service';
import { ConnectionUtils } from '@3d-scene/model3d/util/connection-utils';

export interface IConnectionSpanData extends ISpanData {
  readonly connection: Connection;
  readonly connectionUid: string;
  readonly metaElement: MetaElementVO;
  readonly metaElementUsage: MetaElementUsageVO;
  readonly editable: boolean;
  description: string;
  terminalPositionOnRail: number;
  compositeArticle: ArticleVO;
  readonly articleOrCompositeArticle: ArticleVO;
}

export interface IArticleSpanData extends ISpanData {
  readonly articleUsageId: string;
}

export function isConnectionSpanData(spanData: ISpanData): spanData is IConnectionSpanData {
  return spanData instanceof ConnectionSpanData;
}

export function isArticleSpanData(spanData: ISpanData): spanData is IArticleSpanData {
  return spanData instanceof ArticleSpanData;
}

export interface ISpanData {
  rowSpan: number;
  colSpan: number;
  data: string;
  html5data: string;
  compression: number;
  autoFormatByTextCheck: boolean; // replaces autoFontSize and autoCompression (autoFormatByTextCheck = autoFontSize || autoCompression)

  labelFormat: LabelFormat;
  defaultTextParameters: DefaultTextParameters;
  readonly horizontalAlign: string; // 'left', 'right' or 'center', only read by label editor

  article: ArticleVO;
  // End plates should now be included as spanData in table provide to produce new columns according to SMAR-53
  isEndPlate: boolean; // true in case spanData represents end plate in label editor.

  // if cell is not enabled, it takes up non-editable space in label editor for layout purposes (marking systems or new: end-plates)
  enabled: boolean;

  containsErrors: boolean; // only set and read by label editor to keep track of span datas with text check violations
  containsStrictConformityErrors: boolean; // only set and read by label editor to keep track of span datas with text check violations
  errorTooltipText: string; // only set and read by label editor to keep track of span datas with text check violations

  // Multiline and merging:
  maxMultilineCount: number; // max possible multiline count for that label material of that spanData
  isMergeWithNext: boolean; // if merge with next SpanData in that row is allowed or not
  currentMultilineCount: number; // only read and set by label editor
  // mergeClass no longer needed in my opinion
  parent: ISpanData; // if cell is part of merge/multiline, then for every spanData (excluding main one) parent points to main spanData
  // lineSpan: dont see any usage for lineSpan anymore

  // text check: this information (including spacing and spacingToNextSpanDataInRow) will be (currently) ignored for spanDatas for endplates
  // spanData of endPlates might be used to store info like spacingToNextSpanDataInRow (if there would be no other cases of such info)
  // Again: At least in current implementation, info is not used here (please lets discuss in case it should/can be used)
  textCheckClass: string;
  spacing: number;
  font: string;
  isDirectPrinting: boolean;
  spacingToNextSpanDataInRow: number;
  spacingData: TextCheckSpacingData;
  cellLabelSizeInfos: TextSizeCheckVO[];
  isTextCheckDirty: boolean; // only read and set by label editor
  getMaxPermittedCharCountPerLine(fontName: string, fontSize: number, fontBold: number, rotation: number, lines: number, multiLineCount): number;
  getMaxPermittedLineInLineCount(
    fontName: string,
    fontSize: number,
    fontBold: number,
    rotation: number,
    minChars: number,
    multiLineCount: number
  ): number;
  getCurrentFontSize(fontName: string, fontBold: number, rotation: number, amountChars: number, amountLines: number, multiLineCount: number): number;
  getDefaultFontSize(): number;

  // only used by lable editor to activate and deactivate spanData for usage of html5data in cells of label editor
  activate(): void;
  deactivate(): void;
  initHtml5Data(): void;
  adjustRowAndColSpanAccordingToPosInTable(row: number, col: number, rowCount: number, colCount: number): void;

  clone(): ISpanData; // cloneing spanData. This is (mostly) a deep clone.

  readonly uniformFontSize: number | undefined;
}

class SpanData implements ISpanData {
  private _rowSpan: number = 1;
  private _colSpan: number = 1;
  private _data: string = null;
  private _html5data: string = null;
  private _compression: number = 100;
  private _autoFormatByTextCheck: boolean = true;
  private _labelFormat: LabelFormat;
  private _defaultTextParameters: DefaultTextParameters;
  private _enabled: boolean;
  private _isEndPlate: boolean = false;
  private _containsErrors: boolean = false;
  private _containsStrictConformityErrors: boolean = false;
  private _errorTooltipText: string = '';
  private _maxMultilineCount: number = 1;
  private _isMergeWithNext: boolean = false;
  private _currentMultilineCount: number = 1;
  private _parent: ISpanData = null;
  private _horizontalAlign: string = null;
  private _font: string = ConfiguratorParameters.labeling.defaultLabelingFontName;

  private _textCheckClass: string = null;
  private _spacing: number = NaN;
  private _spacingToNextSpanDataInRow: number = NaN;
  private _cellLabelSizeInfos: TextSizeCheckVO[] = null;
  private _isTextCheckDirty: boolean = false;

  private _html5DataWasChanged: boolean = false;
  private _html5DataRepresentsNullValue: boolean = false;

  private _isActivated: boolean = true;

  // set by label editor component:
  public useStrictTextCheckConformity: boolean = null;

  public readonly uniformFontSize: number | undefined;

  constructor(
    protected localeResourceManager: LocaleResourceManager,
    data: string = null,
    labelFormat: LabelFormat = null,
    cellLabelSizeInfos: TextSizeCheckVO[] = null,
    uniformFontSize: number | undefined = undefined,
  ) {
    this._enabled = data !== null;
    this._labelFormat = labelFormat === null ? new LabelFormat() : labelFormat;
    this._defaultTextParameters = new DefaultTextParameters();
    this.data = data;
    this._html5DataWasChanged = false;
    this._cellLabelSizeInfos = cellLabelSizeInfos;
    this.uniformFontSize = uniformFontSize;
  }

  private translateDataToHtml5Data(): void {
    if (this._data) {
      const html5DataDocument: Document = LabelEditorUtils.convertDataToHTML5DataDocument(this._data, this);
      LabelEditorUtils.applyCustomFormatSettingsToHTML5DataDocument(
        html5DataDocument,
        NaN,
        this.labelFormat.direction,
        this.labelFormat.verticalAlign,
        this.compression,
        this.autoFormatByTextCheck,
        false /* first we need to create and set html5 data, before we can validate it in next call */,
        false /* first we need to create and set html5 data, before we can validate it in next call */,
        null,
        this.font,
      );
      this._html5data = LabelEditorUtils.getHtml5dataFromDocument(html5DataDocument);
      // this is necessary, because only now initial html5data for spanData exists and can be extended by validation result:
      const checkResults: ConstraintCheckResults = ConstraintCheckUtils.performValidationOnSpanData(
        this.useStrictTextCheckConformity,
        this.localeResourceManager,
        this
      );
      if (this._data) {
        this.containsErrors = checkResults.containsErrors;
        this.containsStrictConformityErrors = checkResults.containsStrictConformityErrors;
        this.errorTooltipText = checkResults.errorTooltipText;
      }
      if (this.autoFormatByTextCheck && !isNaN(checkResults.textCompression)) {
        this.compression = checkResults.textCompression;
      }
      LabelEditorUtils.applyCustomFormatSettingsToHTML5DataDocument(
        html5DataDocument,
        LabelEditorUtils.getEffectiveFontSize(this, checkResults),
        this.labelFormat.direction,
        this.labelFormat.verticalAlign,
        this.compression,
        this.autoFormatByTextCheck,
        this.containsErrors,
        this.containsStrictConformityErrors,
        this.errorTooltipText,
        this.font,
      );
      this._html5data = LabelEditorUtils.getHtml5dataFromDocument(html5DataDocument);
    } else {
      this._html5data = LabelEditorUtils.createEmptyHTML5Data(this.getDefaultFontSize(), this.defaultTextParameters.bold, this.font);
    }
    this._html5DataWasChanged = false;
    this._html5DataRepresentsNullValue = this._data === null;
  }

  public get data(): string {
    if (this._html5DataWasChanged && this.enabled) {
      if (this._html5DataRepresentsNullValue) {
        this._data = null;
      } else {
        this._data = LabelEditorUtils.convertHTML5DataToData(this._html5data);
      }
      this._html5DataWasChanged = false;
    }
    return this._data;
  }

  public set data(value: string) {
    // set null in case value is null or undefined (since label-editor component checks null explicitly)
    this._data = StringUtils.isNull(value) ? null : LabelHtmlSanitizer.sanitize(value);
    this._html5data = null;
    this._horizontalAlign = null;
  }

  public activate(): void {
    this._isActivated = true;
  }

  public deactivate(): void {
    this._isActivated = false;
  }

  public get enabled(): boolean {
    return this._enabled;
  }

  public set enabled(value: boolean) {
    this._enabled = value;
  }

  public get isEndPlate(): boolean {
    return this._isEndPlate;
  }

  public set isEndPlate(value: boolean) {
    this._isEndPlate = value;
  }

  public get article(): ArticleVO {
    return null;
  }

  public get containsErrors(): boolean {
    return this._containsErrors;
  }

  public set containsErrors(value: boolean) {
    this._containsErrors = value;
  }

  public get containsStrictConformityErrors(): boolean {
    return this._containsStrictConformityErrors;
  }

  public set containsStrictConformityErrors(value: boolean) {
    this._containsStrictConformityErrors = value;
  }

  public get errorTooltipText(): string {
    return this._errorTooltipText;
  }

  public set errorTooltipText(value: string) {
    this._errorTooltipText = value;
  }

  public get html5data(): string {
    if (!this._isActivated) {
      return null;
    }
    this.initHtml5Data();
    return this._html5data;
  }

  public initHtml5Data(): void {
    if (this._html5data === null) {
      this.translateDataToHtml5Data();
    }
  }

  public set html5data(value: string) {
    if (!this._isActivated) {
      return;
    }
    if (value === this._html5data) {
      // handsontable presets values again on init for in view elements,
      // these are not changes/edited values from user so html5data was not changed
      return;
    }
    if (value === null) {
      this._html5DataRepresentsNullValue = true;
    } else {
      this._html5DataRepresentsNullValue = false;
    }
    if (value === null || value === '') {
      // Handsontable can wipe cell content by setting empty string when splitting cells or using delete key
      // -> we need to keep formatting for empty cells.
      // null content will be set by handsontable in case cells are merged as part of bigger cell.
      // We also allow null or empty content for a disabled cell, so that in case user selected such cell as lead (which cannot be avoided)
      // the format changes work as expected for additionally selected enabled cells.
      value = LabelEditorUtils.createEmptyHTML5Data(this.getDefaultFontSize(), this.defaultTextParameters.bold, this.font);
    }
    LabelEditorUtils.parseCustomFormatSettingsIntoSpanDataFromHTML5Data(this, value);
    this._html5data = value;
    this._horizontalAlign = null;
    this._html5DataWasChanged = true;
  }

  public get rowSpan(): number {
    return this._rowSpan;
  }

  public set rowSpan(value: number) {
    this.updateRowColSpan(value, this._colSpan);
  }

  public get colSpan(): number {
    return this._colSpan;
  }

  public set colSpan(value: number) {
    this.updateRowColSpan(this._rowSpan, value);
  }

  private updateRowColSpan(rowSpan: number, colSpan: number): void {
    this._colSpan = colSpan;
    this._rowSpan = rowSpan;
  }

  public get compression(): number {
    return this._compression;
  }

  public set compression(compression: number) {
    this._compression = compression;
  }

  public get autoFormatByTextCheck(): boolean {
    return this._autoFormatByTextCheck;
  }

  public set autoFormatByTextCheck(autoFormatByTextCheck: boolean) {
    this._autoFormatByTextCheck = autoFormatByTextCheck;
  }

  public get labelFormat(): LabelFormat {
    return this._labelFormat;
  }

  public set labelFormat(labelFormat: LabelFormat) {
    this._labelFormat = labelFormat;
  }

  public get defaultTextParameters(): DefaultTextParameters {
    return this._defaultTextParameters;
  }

  public set defaultTextParameters(defaultTextParameters: DefaultTextParameters) {
    this._defaultTextParameters = defaultTextParameters;
  }

  public get maxMultilineCount(): number {
    return this._maxMultilineCount;
  }

  public set maxMultilineCount(maxMultilineCount: number) {
    this._maxMultilineCount = maxMultilineCount;
  }

  public get isMergeWithNext(): boolean {
    return this._isMergeWithNext;
  }

  public set isMergeWithNext(isMergeWithNext: boolean) {
    this._isMergeWithNext = isMergeWithNext;
  }

  public get parent(): ISpanData {
    return this._parent;
  }

  public set parent(parent: ISpanData) {
    this._parent = parent;
  }

  public get horizontalAlign(): string {
    if (this._horizontalAlign === null) {
      if (!this._html5data) {
        return 'left';
      }
      if (this._html5data.indexOf('text-align: center') > -1) {
        this._horizontalAlign = 'center';
      } else if (this._html5data.indexOf('text-align: right') > -1) {
        this._horizontalAlign = 'right';
      } else {
        this._horizontalAlign = 'left';
      }
    }
    return this._horizontalAlign;
  }

  public get currentMultilineCount(): number {
    return this._currentMultilineCount;
  }

  public set currentMultilineCount(currentMultilineCount: number) {
    this._currentMultilineCount = currentMultilineCount;
  }

  public get textCheckClass(): string {
    return this._textCheckClass;
  }

  public set textCheckClass(value: string) {
    this._textCheckClass = value;
  }

  public get spacing(): number {
    return this._spacing;
  }

  public set spacing(spacing: number) {
    this._spacing = spacing;
  }

  public get font(): string {
    return this._font;
  }

  public set font(value: string) {
    if (Log.assertExists(value, 'font')) {
      this._font = value;
    }
  }

  public get isDirectPrinting(): boolean {
    return false;
  }

  public get spacingToNextSpanDataInRow(): number {
    return this._spacingToNextSpanDataInRow;
  }

  public set spacingToNextSpanDataInRow(spacingToNextSpanDataInRow: number) {
    this._spacingToNextSpanDataInRow = spacingToNextSpanDataInRow;
  }

  public get spacingData(): TextCheckSpacingData {
    return new TextCheckSpacingData(this.spacing);
  }

  public get cellLabelSizeInfos(): TextSizeCheckVO[] {
    return this._cellLabelSizeInfos;
  }

  public set cellLabelSizeInfos(cellLabelSizeInfos: TextSizeCheckVO[]) {
    this._cellLabelSizeInfos = cellLabelSizeInfos;
  }

  public get isTextCheckDirty(): boolean {
    return this._isTextCheckDirty;
  }

  public set isTextCheckDirty(isTextCheckDirty: boolean) {
    this._isTextCheckDirty = isTextCheckDirty;
  }

  // NGI-362
  /**
   * Returns the max permitted char count per line.
   *
   * Pass -1 for a numeric parameter to ignore it and "" for a string parameter to ignore it
   *
   * @param cellLabelSizeInfos Array of CellLabelSizeInfo elements to choose from
   * @param fontName name of font, or "" to ignore
   * @param fontSize fontsize, or -1 to ignore
   * @param fontBold 0 or 1, or -1 to ignore
   * @param rotation rotation yes or no (0 or 1), or -1 to ignore
   * @param lines number of lines that is used
   */
  public getMaxPermittedCharCountPerLine(
    fontName: string = null,
    fontSize: number = NaN,
    fontBold: number = NaN,
    rotation: number = NaN,
    lines: number = NaN,
    multiLineCount: number = 1
  ): number {
    const cellLabelSizeInfosToCheck: TextSizeCheckVO[] = CellLabelSizeInfoUtils.getMultiLineAdjustedCellLabelSizeInfos(
      this.cellLabelSizeInfos,
      multiLineCount,
      rotation
    );
    let charCount: number = NaN;
    if (cellLabelSizeInfosToCheck !== null) {
      for (const clsi of cellLabelSizeInfosToCheck) {
        if (
          clsi !== null &&
          (!fontName || fontName === clsi.fontName) &&
          (isNaN(fontSize) || fontSize === clsi.fontSize) &&
          (isNaN(fontBold) || fontBold === clsi.fontBold) &&
          (isNaN(rotation) || rotation === clsi.rotation) &&
          (isNaN(lines) || clsi.lines === lines)
        ) {
          charCount = isNaN(charCount) ? clsi.chars : Math.max(charCount, clsi.chars);
        }
      }
    }
    return charCount;
  }

  /**
   * Retruns the max permitted line count.
   *
   * Pass -1 for a numeric parameter to ignore it and "" for a string parameter to ignore it
   *
   * @param cellLabelSizeInfos Array of CellLabelSizeInfo elements to choose from
   * @param fontName name of font, or "" to ignore
   * @param fontSize fontsize, or -1 to ignore
   * @param fontBold 0 or 1, or -1 to ignore
   * @param chars number of lines, or -1 to ignore
   * @param rotation rotation yes or no (0 or 1), or -1 to ignore
   * @param minChars lowest number of chars that is used
   */
  public getMaxPermittedLineInLineCount(
    fontName: string = null,
    fontSize: number = NaN,
    fontBold: number = NaN,
    rotation: number = NaN,
    minChars: number = NaN,
    multiLineCount: number = 1
  ): number {
    const cellLabelSizeInfosToCheck: TextSizeCheckVO[] = CellLabelSizeInfoUtils.getMultiLineAdjustedCellLabelSizeInfos(
      this.cellLabelSizeInfos,
      multiLineCount,
      rotation
    );
    let lineCount: number = NaN;
    if (cellLabelSizeInfosToCheck !== null) {
      for (const clsi of cellLabelSizeInfosToCheck) {
        if (
          clsi !== null &&
          (!fontName || fontName === clsi.fontName) &&
          (isNaN(fontSize) || fontSize === clsi.fontSize) &&
          (isNaN(fontBold) || fontBold === clsi.fontBold) &&
          (isNaN(rotation) || rotation === clsi.rotation) &&
          (isNaN(minChars) || clsi.chars >= minChars)
        ) {
          lineCount = isNaN(lineCount) ? clsi.lines : Math.max(lineCount, clsi.lines);
        }
      }
    }
    return lineCount;
  }

  public getDefaultFontSize(): number {
    const defaultFontSizeByTextCheck: number = this.getCurrentFontSize(
      null,
      this.defaultTextParameters.bold ? 1 : 0,
      0,
      1,
      1,
      this.currentMultilineCount
    );
    return !isNaN(defaultFontSizeByTextCheck) ? defaultFontSizeByTextCheck : this.defaultTextParameters.fontSize;
  }

  public getCurrentFontSize(
    fontName: string = null,
    fontBold: number = NaN,
    rotation: number = NaN,
    amountChars: number = NaN,
    amountLines: number = NaN,
    multiLineCount: number = 1
  ): number {
    const bestMatch: TextSizeCheckVO | undefined = TextSizeCheckUtils.getBestMatchingTextSizeCheck(this.cellLabelSizeInfos, {
      fontName,
      fontBold,
      rotation,
      chars: amountChars,
      lines: amountLines,
      multiLineCount,
    });
    return this.uniformFontSize ?? bestMatch?.fontSize ?? Number.NaN;
  }

  public adjustRowAndColSpanAccordingToPosInTable(row: number, col: number, rowCount: number, colCount: number): void {
    this.updateRowColSpan(Math.min(row + this.rowSpan, rowCount) - row, Math.min(col + this.colSpan, colCount) - col);
  }

  public clone(): ISpanData {
    const clone: SpanData = new SpanData(this.localeResourceManager);
    this.fillClonedObject(clone);
    return clone;
  }

  protected fillClonedObject(clone: SpanData): void {
    clone._rowSpan = this._rowSpan;
    clone._colSpan = this._colSpan;
    clone._data = this._data;
    clone._html5data = this._html5data;
    clone._compression = this._compression;
    clone._autoFormatByTextCheck = this._autoFormatByTextCheck;
    clone._labelFormat = this._labelFormat === null ? this._labelFormat : this._labelFormat.clone();
    clone._defaultTextParameters = this._defaultTextParameters; // not deep cloned
    clone._enabled = this._enabled;
    clone._isEndPlate = this._isEndPlate;
    clone._containsErrors = this._containsErrors;
    clone._containsStrictConformityErrors = this._containsStrictConformityErrors;
    clone._errorTooltipText = this._errorTooltipText;
    clone._maxMultilineCount = this._maxMultilineCount;
    clone._isMergeWithNext = this._isMergeWithNext;
    clone._currentMultilineCount = this._currentMultilineCount;
    clone._parent = this._parent; // not deep cloned
    clone._horizontalAlign = this._horizontalAlign;
    clone._font = this._font;

    clone._textCheckClass = this._textCheckClass;
    clone._spacing = this._spacing;
    clone._spacingToNextSpanDataInRow = this._spacingToNextSpanDataInRow;
    clone._cellLabelSizeInfos = this._cellLabelSizeInfos; // not deep cloned
    clone._isTextCheckDirty = this._isTextCheckDirty;

    clone._html5DataWasChanged = this._html5DataWasChanged;
    clone._html5DataRepresentsNullValue = this._html5DataRepresentsNullValue;

    clone._isActivated = this._isActivated;

    clone.localeResourceManager = this.localeResourceManager; // not deep cloned
    clone.useStrictTextCheckConformity = this.useStrictTextCheckConformity;
  }
}

class ConnectionSpanData extends SpanData implements IConnectionSpanData {
  private _connection: Connection;
  private _compositeArticle: ArticleVO;
  private _editable: boolean = false;
  private _description: string = null;
  private _terminalPositionOnRail: number = null;

  constructor(
    localeResourceManager: LocaleResourceManager,
    connection: Connection,
    uniformFontSize: number | undefined,
  ) {
    super(
      localeResourceManager,
      null,
      null,
      null,
      uniformFontSize,
    );
    this.connection = connection;
    this.enabled = true;
  }

  public get connection(): Connection {
    return this._connection;
  }

  public override getDefaultFontSize(): number {
    const series = this.articleOrCompositeArticle.series;

    const useSeriesDefinedFontSize = series
      && this.isDirectPrinting
      && this.autoFormatByTextCheck
      && ArticleUtils.isMcsGdsProjectArticle(this.articleOrCompositeArticle);

    if (useSeriesDefinedFontSize) {
      const fontSizeBySeries = ConfiguratorParameters.labeling.directPrintingFontSizes.get(series);
      if(fontSizeBySeries) return fontSizeBySeries;
    }

    return super.getDefaultFontSize();
  }

  public set connection(value: Connection) {
    this._connection = value;
    if (this.metaElement && this.metaElement instanceof TextureAreaVO) {
      const textMeta: TextureAreaVO = this.metaElement as TextureAreaVO;
      if (ArticleUtils.isTextTextureType(textMeta.textureType)) {
        if (textMeta.defaultValueAsText) {
          this.data = textMeta.defaultValueAsText.text;
          this._editable = !this.data || StringUtils.trim(this.data) === '';
        } else {
          this._editable = true;
        }
        this.enabled = this._editable;
      }
    }
  }

  public get connectionUid(): string {
    return this._connection.uid;
  }

  public get metaElement(): MetaElementVO {
    return this._connection.metaElementVO;
  }

  public get metaElementUsage(): MetaElementUsageVO {
    return this._connection.metaElementUsage;
  }

  public get article(): ArticleVO {
    return this._connection.article;
  }

  public get editable(): boolean {
    return this._editable;
  }

  public get description(): string {
    return this._description || '';
  }

  public set description(description: string) {
    this._description = description;
  }

  public get terminalPositionOnRail(): number {
    return this._terminalPositionOnRail;
  }

  public set terminalPositionOnRail(positionOnRail: number) {
    this._terminalPositionOnRail = positionOnRail;
  }

  get articleOrCompositeArticle(): ArticleVO {
    return this._compositeArticle ?? this.article;
  }

  set compositeArticle(value: ArticleVO) {
    this._compositeArticle = value;
  }

  public override get isDirectPrinting(): boolean {
    return !ArticleUtils.isLabelingArticle(this._connection.articleContainer.article);
  }

  public clone(): ISpanData {
    const clone: ConnectionSpanData = new ConnectionSpanData(this.localeResourceManager, this.connection, this.uniformFontSize);
    this.fillClonedObject(clone);
    clone._connection = this._connection; // not deep cloned
    clone._editable = this._editable;
    return clone;
  }
}

class ArticleSpanData extends SpanData implements IArticleSpanData {
  private _articleUsageId: string = null;

  constructor(
    localeResourceManager: LocaleResourceManager,
    articleUsageId: string,
    private readonly containerService: ContainerService,
  ) {
    super(localeResourceManager);
    this._articleUsageId = articleUsageId;
  }

  public get articleUsageId(): string {
    return this._articleUsageId;
  }

  public get article(): ArticleVO {
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(this._articleUsageId);
    return !articleContainer ? null : articleContainer.article;
  }

  public clone(): ISpanData {
    const clone: ArticleSpanData = new ArticleSpanData(this.localeResourceManager, this._articleUsageId, this.containerService);
    this.fillClonedObject(clone);
    return clone;
  }
}

@Injectable({ providedIn: 'root' })
export class SpanDataFactory {
  constructor(
    private readonly localeResourceManager: LocaleResourceManager,
    private readonly containerService: ContainerService,
    private readonly directPrintingService: DirectPrintingService,
  ) {}

  /** Creates a base SpanData instance. */
  public createSpanData(
    data: string = null,
    labelFormat: LabelFormat = null,
    cellLabelSizeInfos: TextSizeCheckVO[] = null
  ): ISpanData {
    return new SpanData(this.localeResourceManager, data, labelFormat, cellLabelSizeInfos);
  }

  /** Creates a ConnectionSpanData instance. */
  public createConnectionSpanData(connection: Connection): IConnectionSpanData {
    const uniformFontSize: number | undefined = ConnectionUtils.isDirectPrintingTextCheckConnection(connection) ?
      this.directPrintingService.uniformFontSize :
      undefined;
    return new ConnectionSpanData(this.localeResourceManager, connection, uniformFontSize);
  }

  /** Creates an ArticleSpanData instance. */
  public createArticleSpanData(articleUsageId: string): IArticleSpanData {
    return new ArticleSpanData(this.localeResourceManager, articleUsageId, this.containerService);
  }
}

